import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  addDays,
  endOfISOWeek,
  format,
  formatDate,
  startOfISOWeek,
} from "date-fns";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { DateRange, Modifiers, rangeIncludesDate } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Calendar } from "./ui/calendar";
import { useLocale } from "@/hooks/useLocale";
import { DEFAULT_DATE_FORMAT, MIN_PROJECT_DATE } from "@/constants";
interface WeekFilterProps {
  className?: string;
  popoverClassName?: string;
  value?: Date;
  onWeekChange?: (date: Date) => void;
  disabled?: boolean;
}

const SelectedWeekLabel = ({
  from,
  to,
}: {
  from: Date | undefined;
  to: Date | undefined;
}) => {
  const { t } = useTranslation();
  if (!from || !to) return null;
  return (
    <div className="flex flex-row gap-2 my-2 text-sm text-muted-foreground">
      {t("weekFilter.weekFrom")}{" "}
      <strong className="text-foreground">
        {formatDate(from, DEFAULT_DATE_FORMAT)}
      </strong>
      {t("weekFilter.weekTo")}{" "}
      <strong className="text-foreground">
        {formatDate(to, DEFAULT_DATE_FORMAT)}
      </strong>
    </div>
  );
};

export default forwardRef<HTMLButtonElement, WeekFilterProps>(
  function WeekFilter(
    { className, popoverClassName, value, onWeekChange, disabled, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const locale = useLocale();
    const [selectedWeek, setSelectedWeek] = useState<DateRange | undefined>(
      value
        ? {
            from: startOfISOWeek(value),
            to: endOfISOWeek(value),
          }
        : undefined,
    );

    const onNextWeekClick = useCallback(() => {
      if (selectedWeek && selectedWeek.from && selectedWeek.to) {
        const range = {
          from: addDays(selectedWeek.from, 7),
          to: addDays(selectedWeek.to, 7),
        };
        setSelectedWeek(range);
      }
    }, [selectedWeek]);

    const onPreviousWeekClick = useCallback(() => {
      if (selectedWeek && selectedWeek.from && selectedWeek.to) {
        const range = {
          from: addDays(selectedWeek.from, -7),
          to: addDays(selectedWeek.to, -7),
        };
        setSelectedWeek(range);
      }
    }, [selectedWeek]);

    const isPreviousWeekDisabled = useCallback(() => {
      if (!selectedWeek || !selectedWeek.from) {
        return true;
      }

      const previousWeekEnd = addDays(selectedWeek.from, -1);

      return previousWeekEnd < MIN_PROJECT_DATE;
    }, [selectedWeek]);

    const handleDayClick = useCallback(
      (day: Date, modifiers: Modifiers) => {
        if (modifiers.selected) {
          setSelectedWeek(undefined); // clear the selection if the day is already selected
          return;
        }
        setSelectedWeek({
          from: startOfISOWeek(day),
          to: endOfISOWeek(day),
        });
      },
      [setSelectedWeek],
    );

    const getISOWeek = (date?: Date) => {
      if (!date) return "";
      return format(date, "I, R");
    };

    useEffect(() => {
      if (selectedWeek?.from) {
        onWeekChange?.(selectedWeek.from);
      }
    }, [selectedWeek?.from, onWeekChange]);

    return (
      <div className={cn("flex flex-row gap-2", className)}>
        <Button
          variant="outline"
          className="grow-0"
          onClick={onPreviousWeekClick}
          disabled={isPreviousWeekDisabled() || disabled}
        >
          <ChevronLeftIcon className="mr-1 h-4 w-4" />
          {t("weekFilter.previousWeek")}
        </Button>
        <Popover>
          <PopoverTrigger asChild className={popoverClassName}>
            <div className="flex flex-row gap-2">
              <Button
                variant={"outline"}
                className={cn(
                  "justify-start text-left font-medium grow-0",
                  !selectedWeek && "text-muted-foreground",
                )}
                {...props}
                ref={ref}
                disabled={disabled}
              >
                <CalendarIcon className="mr-1 h-4 w-4" />
                {selectedWeek ? (
                  <>
                    {t("weekFilter.week")} {getISOWeek(selectedWeek?.from)}
                  </>
                ) : (
                  <span>{t("weekFilter.pickWeek")}</span>
                )}
              </Button>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              showWeekNumber
              defaultMonth={selectedWeek?.from}
              showOutsideDays
              startMonth={MIN_PROJECT_DATE}
              disabled={(date) => date < MIN_PROJECT_DATE}
              modifiers={{
                selected: selectedWeek,
                range_start: selectedWeek?.from,
                range_end: selectedWeek?.to,
                range_middle: (date: Date) =>
                  selectedWeek
                    ? rangeIncludesDate(selectedWeek, date, true)
                    : false,
              }}
              onDayClick={handleDayClick}
              footer={
                <SelectedWeekLabel
                  from={selectedWeek?.from}
                  to={selectedWeek?.to}
                />
              }
              classNames={{
                range_start:
                  "transition-colors !bg-slate-400 [&>button]:!bg-transparent rounded-l-lg",
                range_middle:
                  "transition-colors !bg-slate-400 [&>button]:!bg-transparent",
                range_end:
                  "transition-colors !bg-slate-400 [&>button]:!bg-transparent rounded-r-lg",
              }}
              locale={locale}
              ISOWeek
            />
          </PopoverContent>
        </Popover>
        <Button
          variant="outline"
          className="grow-0"
          onClick={onNextWeekClick}
          disabled={!selectedWeek || disabled}
        >
          {t("weekFilter.nextWeek")}
          <ChevronRightIcon className="ml-1 h-4 w-4" />
        </Button>
      </div>
    );
  },
);
